import React, { useEffect, useMemo, useState } from "react";
import {
  Empty,
  Space,
  Tag,
  Typography,
  message,
  Form,
  Button,
  DatePicker,
  TimePicker,
  Modal,
  Segmented,
} from "antd";
import dayjs from "dayjs";
import { LeftOutlined } from "@ant-design/icons";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import tagStyles from "../../../../styles/Custom/Tag.module.css";
import useQuery from "../../../../hooks/useQuery";
import {
  changeIpBedApi,
  updateIpBedApi,
} from "../../../../services/receptionist.services";
import { formatToINR } from "../../../../helpers/utility";
import styles from "./BedCards.module.css";
import occupancyIcon from "../../../../assets/receptionistIcons/bedMgmtOccupancy.svg";
import acIcon from "../../../../assets/receptionistIcons/bedMgmtAC.svg";
import nonAcIcon from "../../../../assets/receptionistIcons/bedMgmtNonAC.svg";

const BedCards = ({
  beds,
  patientData,
  refreshPage,
  refreshBedData,
  editBed,
  setEditBed,
  setSwitchBed,
  showSelectionCancel = false,
  onCancelSelection,
  buildings = [],
  buildingId,
  buildingStats = null,
  onBuildingChange,
}) => {
  const params = useQuery();
  const [form] = Form.useForm();
  const [visiblePopover, setVisiblePopover] = useState(null); // Track which popover is visible
  const [editListData, setEditListData] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const bedId = patientData?.admission?.bed?.id;
  const admissionDate = patientData?.admission?.admission_date
    ? dayjs(patientData.admission.admission_date).startOf("day")
    : null;

  const disabledDate = (current) => {
    if (!current) return false;
    if (admissionDate && current.isBefore(admissionDate, "day")) return true;
    return false;
  };

  const summary = useMemo(() => {
    const total = Number(buildingStats?.total_beds || 0);
    const occupied = Number(buildingStats?.total_occupied_beds || 0);
    const available = Math.max(0, total - occupied);
    const acTotal = Number(buildingStats?.total_ac_beds || 0);
    const acOccupied = Number(buildingStats?.total_ac_beds_occupied || 0);
    const nonAcTotal = Number(buildingStats?.total_non_ac_beds || 0);
    const nonAcOccupied = Number(buildingStats?.total_non_ac_beds_occupied || 0);

    return {
      total,
      available,
      occupied,
      pct: total ? Math.round((occupied / total) * 100) : 0,
      ac: {
        total: acTotal,
        occupied: acOccupied,
        pct: acTotal ? Math.round((acOccupied / acTotal) * 100) : 0,
      },
      nonAc: {
        total: nonAcTotal,
        occupied: nonAcOccupied,
        pct: nonAcTotal ? Math.round((nonAcOccupied / nonAcTotal) * 100) : 0,
      },
    };
  }, [buildingStats]);

  const filteredBeds = useMemo(() => {
    return (beds || []).map((group) => {
      const bedData = (group?.bed_data || []).filter((bed) => {
        const isOccupied = !!bed?.status;
        const isAc = !!bed?.room__room_type__ac;

        const statusMatch =
          statusFilter === "all" ||
          (statusFilter === "occupied" && isOccupied) ||
          (statusFilter === "available" && !isOccupied);

        const typeMatch =
          typeFilter === "all" ||
          (typeFilter === "ac" && isAc) ||
          (typeFilter === "non_ac" && !isAc);

        return statusMatch && typeMatch;
      });

      return {
        ...group,
        bed_data: bedData,
      };
    });
  }, [beds, statusFilter, typeFilter]);

  const onFinish = (values) => {
    let startMoment;
    if (isMobile) {
      const d = values?.mobile_bed_date;
      const t = values?.mobile_bed_time;
      if (!d || !t) {
        message.error("Please select date and time");
        return;
      }
      startMoment = dayjs(d)
        .hour(dayjs(t).hour())
        .minute(dayjs(t).minute())
        .second(0)
        .millisecond(0);
    } else {
      startMoment = dayjs(values.datetime);
    }
    const combinedDateTime = startMoment.utc();
    const formData = {
      bed_start_date: combinedDateTime.format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]"),
      bed: editListData?.id,
    };

    {
      if (editBed || !bedId) {
        updateIpBedApi(params?.patient, formData)
          .then((res) => {
            message.success(res?.data?.message);
            setVisiblePopover(null);
            refreshPage();
            refreshBedData();
            form.resetFields();
            setEditBed(false);
          })
          .catch((err) => {
            message.error(err?.response?.data?.message);
          });
      } else {
        changeIpBedApi(params?.patient, formData)
          .then((res) => {
            message.success(res?.data?.message);
            setVisiblePopover(null);
            refreshPage();
            refreshBedData();
            form.resetFields();
            setSwitchBed(false);
          })
          .catch((err) => {
            message.error(err?.response?.data?.message);
          });
      }
    }
  };

  const handleModalClose = () => {
    setVisiblePopover(null);
    form.resetFields();
  };

  return (
    <>
      <div className={styles.page}>
        <div className={styles.summaryHeader}>
          {showSelectionCancel ? (
            <LeftOutlined
              onClick={onCancelSelection}
              className={styles.summaryBackBtn}
              aria-label="Back"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onCancelSelection?.();
              }}
            />
          ) : null}
          <div className={styles.summaryHeaderText}>
            <p className={styles.summaryTitle}>Bed management</p>
            <p className={styles.summarySub}>
              {summary.total} total · {summary.occupied} occupied ·{" "}
              {summary.available} available
            </p>
          </div>
        </div>

        {buildings.length > 1 && onBuildingChange && buildingId != null ? (
          <div className={styles.buildingSegmentRow}>
            <Segmented
              value={buildingId}
              onChange={onBuildingChange}
              options={buildings.map((b) => ({ label: b.name, value: b.id }))}
            />
          </div>
        ) : null}

        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTop}>
              <div>
                <div className={styles.summaryCardLabelRow}>
                  <img
                    src={occupancyIcon}
                    alt=""
                    className={styles.summaryIcon}
                  />
                  <div className={styles.summaryCardLabel}>Bed Occupancy</div>
                </div>
                <div className={styles.summaryCardMeta}>
                  {summary.occupied}/{summary.total} occupied
                </div>
              </div>
              <div
                className={`${styles.summaryCardPercent} ${styles.summaryCardPercentBlue}`}
              >
                {summary.pct}%
              </div>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{
                  width: `${summary.pct}%`,
                  background: "#2E90FA",
                }}
              />
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTop}>
              <div>
                <div className={styles.summaryCardLabelRow}>
                  <img src={acIcon} alt="" className={styles.summaryIcon} />
                  <div className={styles.summaryCardLabel}>AC Beds</div>
                </div>
                <div className={styles.summaryCardMeta}>
                  {summary.ac.occupied}/{summary.ac.total} occupied
                </div>
              </div>
              <div
                className={`${styles.summaryCardPercent} ${styles.summaryCardPercentOrange}`}
              >
                {summary.ac.pct}%
              </div>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{
                  width: `${summary.ac.pct}%`,
                  background: "#F79009",
                }}
              />
            </div>
          </div>

          <div className={styles.summaryCard}>
            <div className={styles.summaryCardTop}>
              <div>
                <div className={styles.summaryCardLabelRow}>
                  <img src={nonAcIcon} alt="" className={styles.summaryIcon} />
                  <div className={styles.summaryCardLabel}>Non AC Beds</div>
                </div>
                <div className={styles.summaryCardMeta}>
                  {summary.nonAc.occupied}/{summary.nonAc.total} occupied
                </div>
              </div>
              <div
                className={`${styles.summaryCardPercent} ${styles.summaryCardPercentPurple}`}
              >
                {summary.nonAc.pct}%
              </div>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{
                  width: `${summary.nonAc.pct}%`,
                  background: "#7F56D9",
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.legendAndFilterRow}>
          <div className={styles.legendRow}>
            <span className={styles.legendItem}>
              <span className={`${styles.dot} ${styles.dotAvailable}`} />
              Available
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.dot} ${styles.dotOccupied}`} />
              Occupied
            </span>
          </div>
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Status:</span>
              <div className={styles.filterPills}>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    statusFilter === "all" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setStatusFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    statusFilter === "occupied" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setStatusFilter("occupied")}
                >
                  Occupied
                </button>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    statusFilter === "available" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setStatusFilter("available")}
                >
                  Available
                </button>
              </div>
            </div>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Type:</span>
              <div className={styles.filterPills}>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    typeFilter === "all" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setTypeFilter("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    typeFilter === "ac" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setTypeFilter("ac")}
                >
                  A/C
                </button>
                <button
                  type="button"
                  className={`${styles.filterPill} ${
                    typeFilter === "non_ac" ? styles.filterPillActive : ""
                  }`}
                  onClick={() => setTypeFilter("non_ac")}
                >
                  Non A/C
                </button>
              </div>
            </div>
          </div>
        </div>

        {filteredBeds.map((group, index) => (
          <div className={styles.wardSection} key={`${group?.floor_no}-${index}`}>
            <div className={styles.wardHeader}>
              <p className={styles.wardTitle}>{group?.floor_no || "Ward"}</p>
              <div className={styles.wardMeta}>
                {Number(group?.bed_data?.length || 0)} beds
              </div>
            </div>

            {group?.bed_data?.length > 0 ? (
              <div className={styles.bedGrid}>
                {group?.bed_data?.map((bed) => {
                  const isOccupied = !!bed?.status;
                  const isCurrentBed = bed?.id === bedId;
                  const isSwitchMode = !!bedId && !editBed;
                  const isClickable = isOccupied
                    ? (isCurrentBed && !isSwitchMode)
                    : true;

                  const cardClass = [
                    styles.bedCard,
                    isOccupied ? styles.bedCardOccupied : styles.bedCardAvailable,
                    !isClickable ? styles.bedCardDisabled : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <div
                      key={bed?.id}
                      className={cardClass}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!isClickable) return;
                        setEditListData(bed);
                        setVisiblePopover(bed?.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" && e.key !== " ") return;
                        if (!isClickable) return;
                        setEditListData(bed);
                        setVisiblePopover(bed?.id);
                      }}
                    >
                      <div className={styles.bedHeader}>
                        <p className={styles.bedNo}>{bed?.bed_no}</p>
                        <span
                          className={[
                            styles.statusBadge,
                            isOccupied
                              ? styles.statusBadgeOccupied
                              : styles.statusBadgeAvailable,
                          ].join(" ")}
                        >
                          {!isOccupied && (
                            <span
                              className={[
                                styles.statusDot,
                                styles.statusDotAvailable,
                              ].join(" ")}
                            />
                          )}
                          {isOccupied ? "Occupied" : "Available"}
                        </span>
                      </div>

                      <span
                        className={[
                          styles.roomChip,
                          isOccupied
                            ? styles.roomChipOccupied
                            : styles.roomChipAvailable,
                        ].join(" ")}
                      >
                        Room no : {bed?.room__room_no || "--"} ·{" "}
                        {bed?.room__room_type__ac ? "AC" : "Non AC"}
                      </span>

                      <div className={styles.bedFooter}>
                        <span className={styles.priceLabel}>Price per day</span>
                        <span
                          className={[
                            styles.bedPrice,
                            !isOccupied
                              ? styles.bedPriceAvailable
                              : styles.bedPriceOccupied,
                          ].join(" ")}
                        >
                          {formatToINR(bed?.cash_price || 0)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty description="No beds in this ward" />
            )}
          </div>
        ))}
      </div>

      <Modal
        open={!!visiblePopover}
        onCancel={handleModalClose}
        footer={null}
        centered
        width={isMobile ? "min(460px, calc(100vw - 24px))" : 460}
        destroyOnClose
        closable={false}
        title={null}
        wrapClassName={styles.bookingBedModalWrap}
        styles={{ body: { padding: 0 } }}
      >
        <div className={styles.bookingModalShell}>
          <div className={styles.bookingModalTop}>
            <div className={styles.bookingModalTopRow}>
              <h3 className={styles.bookingModalTitle}>
                Book Bed: {editListData?.bed_no}
              </h3>
              <button
                type="button"
                className={styles.bookingModalClose}
                onClick={handleModalClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <Space size={6} style={{ marginTop: 8 }} wrap>
              <Tag color="purple" className={tagStyles?.mdRndTag}>
                Room: {editListData?.room__room_no}
              </Tag>
              <Tag color="blue" className={tagStyles?.mdRndTag}>
                Type: {editListData?.room__room_type__name}
              </Tag>
              <Tag color={editListData?.room__room_type__ac ? "cyan" : "default"} className={tagStyles?.mdRndTag}>
                {editListData?.room__room_type__ac ? "AC" : "Non AC"}
              </Tag>
              <Tag color="green" className={tagStyles?.mdRndTag}>
                {formatToINR(editListData?.cash_price)}/day
              </Tag>
            </Space>
          </div>

          <div className={styles.bookingModalMiddle}>
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              className={styles.bookingForm}
              initialValues={{
                datetime: dayjs(),
                mobile_bed_date: dayjs(),
                mobile_bed_time: dayjs(),
              }}
            >
              {isMobile ? (
                <>
                  <Form.Item
                    name="mobile_bed_date"
                    label="Date"
                    rules={[{ required: true, message: "Please select a date" }]}
                  >
                    <DatePicker
                      allowClear={false}
                      className={inputStyles.mdDateInput}
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      disabledDate={disabledDate}
                      placement="bottomLeft"
                      getPopupContainer={() => document.body}
                      popupClassName="curengo-mobile-picker"
                      popupStyle={{
                        maxWidth: "min(100vw - 24px, 360px)",
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="mobile_bed_time"
                    label="Time"
                    rules={[{ required: true, message: "Please select a time" }]}
                  >
                    <TimePicker
                      allowClear={false}
                      className={inputStyles.mdDateInput}
                      format="HH:mm"
                      needConfirm
                      style={{ width: "100%" }}
                      getPopupContainer={() => document.body}
                      popupClassName="curengo-mobile-time-picker"
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  name="datetime"
                  label="Select date & time"
                  rules={[
                    { required: true, message: "Please select date & time" },
                  ]}
                >
                  <DatePicker
                    showTime={{ format: "HH:mm" }}
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={disabledDate}
                  />
                </Form.Item>
              )}
            </Form>
          </div>

          <div className={styles.bookingModalFooter}>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button type="primary" onClick={() => form.submit()}>
              Confirm booking
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BedCards;
